import { produce } from 'immer';

import { mainActionTypes } from './main.types';

const initialState = {
    // diagram details (title, description, db_engine)
    diagramDetails: null,
    diagramDbEngine: null,

    // diagram errors (not found)
    isDiagramNotFound: false,
    isQuickShareLinkInvalid: false,

    // modal diagram rename
    isModalDiagramRenameEnabled: false,
    modalDiagramRenameData: { id: null, diagram: null },

    // modal diagram details
    isModalDiagramDetailsEnabled: false,
    modalDiagramDetailsData: { id: null, diagram: null },

    // modal feedback / support
    isModalSupportEnabled: false,
    modalSupportData: { type: null },
    feedbackSentStatus: null,

    // modal feedback thanks
    isModalFeedbackThanksEnabled: false,
    modalFeedbackThanksData: { type: 'feedback' },  // ? { type: null }

    // modal diagram quick share
    isModalDiagramQuickShareEnabled: false,
    modalDiagramQuickShareData: { id: null, diagram: null },

    // modal diagram create new
    isModalDiagramCreateNewEnabled: false,

    // modal languages
    isModalLanguagesEnabled: false,
    lang: 'en',

};

function unfocusEnabledControls(state, draft) {
    if (state.isModalDiagramRenameEnabled) {
        draft.isModalDiagramRenameEnabled = false;
        draft.modalDiagramRenameData = { id: null, diagram: null };
    }
    if (state.isModalDiagramDetailsEnabled) {
        draft.isModalDiagramDetailsEnabled = false;
        draft.modalDiagramDetailsData = { id: null, diagram: null };
    }
    if (state.isModalSupportEnabled) {
        draft.isModalSupportEnabled = false;
        draft.modalSupportData = { type: null };
    }
    return draft;
}

const mainReducer = (state = initialState, action) => {
    if (state.isStoredState) {
        state = { ...initialState, ...state, isStoredState: false };
    }

    return produce(state, (draft) => {
        switch (action.type) {
            // ------------------------------------------------------------------
            // CANVAS STARTED
            case mainActionTypes.MAIN_CANVAS_STARTED: {
                draft = unfocusEnabledControls(state, draft);
                break;
            }
            // ------------------------------------------------------------------
            // FETCH DIAGRAM
            case mainActionTypes.MAIN_FETCH_DIAGRAM_REQUEST: {
                draft.loading = true;
                break;
            }
            case mainActionTypes.MAIN_FETCH_DIAGRAM_SUCCESS: {
                draft.loading = false;
                draft.diagramDetails = action.payload.data;
                draft.diagramDbEngine = action.payload.data?.db_engine;
                draft.error = null;
                break;
            }
            case mainActionTypes.MAIN_FETCH_DIAGRAM_FAILURE: {
                draft.loading = false;
                draft.error = action.payload;

                // handle differents errors, not-found/access-denied
                draft.isDiagramNotFound = true;
                break;
            }
            // ------------------------------------------------------------------
            // FETCH DIAGRAM ERRORS (NOT FOUND) CLEAR (AFTER FETCH DIAGRAM)
            case mainActionTypes.MAIN_CLEAR_DIAGRAM_ERRORS: {
                draft.isDiagramNotFound = false;
                break;
            }
            // ------------------------------------------------------------------
            // MODAL DIAGRAM RENAME
            case mainActionTypes.MAIN_DIAGRAM_RENAME_REQUEST: {
                if (state.diagramDetails) {
                    if (action.payload?.body?.title) {
                        draft.diagramDetails.title = action.payload?.body?.title;
                    }
                    if (action.payload?.body?.description || typeof action.payload?.body?.description === 'string') {
                        draft.diagramDetails.description = action.payload?.body?.description;
                    }
                }
                break;
            }
            // ------------------------------------------------------------------
            // MODAL DIAGRAM DETAILS
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_DETAILS: {
                const diagramId = action.payload.id || null;

                draft.isModalDiagramDetailsEnabled = !state.isModalDiagramDetailsEnabled;
                draft.modalDiagramDetailsData = { id: null, diagram: null };

                draft.modalDiagramDetailsData = {
                    id: diagramId,
                    diagram: state.diagramDetails,
                };
                break;
            }
            // ------------------------------------------------------------------
            // MODAL FEEDBACK / SUPPORT
            case mainActionTypes.TOGGLE_MODAL_SUPPORT: {
                const type = action.payload.type;
                draft.isModalSupportEnabled = !state.isModalSupportEnabled;
                draft.modalSupportData = { type };
                break;
            }
            case mainActionTypes.SUPPORT_REQ_SUCCESS: {
                draft.feedbackSentStatus = 'success';
                break;
            }
            case mainActionTypes.SUPPORT_REQ_FAILURE: {
                draft.feedbackSentStatus = 'error';
                break;
            }
            case mainActionTypes.MAIN_CLEAR_FEEDBACK_SENT_STATUS: {
                draft.feedbackSentStatus = null;
                break;
            }
            // MODAL FEEDBACK THANKS
            case mainActionTypes.TOGGLE_MODAL_FEEDBACK_THANKS: {
                const type = action.payload.type;
                draft.isModalFeedbackThanksEnabled = !state.isModalFeedbackThanksEnabled;
                draft.modalFeedbackThanksData = { type };
                break;
            }
            // ------------------------------------------------------------------
            // MODAL DIAGRAM QUICK SHARE
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_QUICK_SHARE: {
                draft.isModalDiagramQuickShareEnabled = !state.isModalDiagramQuickShareEnabled;
                // draft.modalDiagramQuickShareData = { type };
                break;
            }
            case mainActionTypes.DISABLE_MODAL_DIAGRAM_QUICK_SHARE: {
                draft.isModalDiagramQuickShareEnabled = false;
                // draft.modalDiagramQuickShareData = { type };
                break;
            }
            // ------------------------------------------------------------------
            // LOAD_NEW_PROJECT
            case mainActionTypes.LOAD_NEW_PROJECT: {
                draft.diagramDetails = {
                    title: 'New Project',
                    description: '',
                    db_engine: 'postgresql'
                };
                draft.diagramDbEngine = 'postgresql';
                draft.isModalDiagramCreateNewEnabled = true;
                break;
            }
            // ------------------------------------------------------------------
            // LOAD QUICK SHARE PROJECT
            case mainActionTypes.LOAD_QUICK_SHARE_PROJECT: {
                if (action.payload.diagramDetails) {
                    draft.diagramDetails = {
                        id: 'quick_share_1',
                        title: action.payload.diagramDetails?.title,
                        description: action.payload.diagramDetails?.description,
                        db_engine: action.payload.diagramDetails?.db_engine,
                    };
                    draft.diagramDbEngine = action.payload.diagramDetails?.db_engine;
                } else {
                    draft.isQuickShareLinkInvalid = true;
                }
                break;
            }
            // ------------------------------------------------------------------
            // LOAD_SAMPLE_PROJECT DETAILS
            case mainActionTypes.LOAD_SAMPLE_PROJECT_DETAILS: {
                draft.diagramDetails = {
                    id: 'sample_1',
                    title: 'Role & Permission Management',
                    // description: `This sample is a lightweight system for managing users, roles, and permissions in modern web apps. It uses role-based access control (RBAC) to clearly define what each user is allowed to do.\n\nIt also includes built-in session tracking and user settings, so you can monitor activity and let users personalize their experience with minimal setup.`,
                    description: `This sample is a lightweight system for managing users, roles, and permissions in modern web apps. It uses role-based access control (RBAC) to clearly define what each user is allowed to do.`,
                    db_engine: 'postgresql',
                };
                draft.diagramDbEngine = 'postgresql';
                break;
            }

            // ------------------------------------------------------------------
            // MODAL DIAGRAM CREATE NEW
            case mainActionTypes.TOGGLE_MODAL_DIAGRAM_CREATE_NEW: {
                draft.isModalDiagramCreateNewEnabled = !state.isModalDiagramCreateNewEnabled;
                break;
            }
            // ADD NEW DIAGRAM
            case mainActionTypes.ADD_NEW_DIAGRAM: {
                draft.diagramDbEngine = action.payload.dbEngine;
                if (draft.diagramDetails) {
                    draft.diagramDetails.db_engine = action.payload.dbEngine;
                }
                break;
            }

            // ------------------------------------------------------------------
            // MODAL LANGUAGES
            case mainActionTypes.TOGGLE_MODAL_LANGUAGES: {
                draft.isModalLanguagesEnabled = !state.isModalLanguagesEnabled;
                break;
            }
            // SET LANGUAGE
            case mainActionTypes.SET_LANGUAGE: {
                // console.log('TTTT', action.payload)
                draft.lang = action.payload.language || 'en';
                break;
            }

            // ------------------------------------------------------------------
            // HANDLE QUICK SHARE ERROR
            case mainActionTypes.HANDLE_QUICK_SHARE_ERROR: {
                draft.isQuickShareLinkInvalid = true;
                break;
            }

            default:
                break;
        }
    });
};

export default mainReducer;
